// card-carousel.ts
// Arrows on a plan card cycle its primary plan's P1-P3 images.

import Base from '../base/base';
import { Catalog, isAsset } from '../../shared/utils/elevation-catalog';
import './card-carousel.scss';

export interface CardCarouselOptions {
  catalog: Catalog;
  /** Cards to scan for a /plans/ link. Default '.kl-card'. */
  cardSelector?: string;
  /** Arrow markup, swappable per instance. Defaults below. */
  prevIcon?: string;
  nextIcon?: string;
  debug?: boolean;
}

const PLAN_LINK = 'a[href^="/plans/"]';

// The brand chevron of the listings filters, turned left and right.
const CHEVRON = (points: string): string =>
  '<svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden="true">' +
  `<path d="${points}" stroke="#1c4344" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const PREV_ICON = CHEVRON('M6.5 1L1.5 6L6.5 11');
const NEXT_ICON = CHEVRON('M1.5 1L6.5 6L1.5 11');

/** The home plan's page images, in P order. */
const IMAGE_FIELDS = ['interior-test', 'secondary-image', 'third-image'];

export default class CardCarousel extends Base {
  private catalog: Catalog;
  private cardSelector: string;
  private prevIcon: string;
  private nextIcon: string;
  private handlers: Array<{ element: HTMLElement; handler: EventListener }> = [];

  constructor(options: CardCarouselOptions) {
    super(options.debug || false);

    this.catalog = options.catalog;
    this.cardSelector = options.cardSelector || '.kl-card';
    this.prevIcon = options.prevIcon || PREV_ICON;
    this.nextIcon = options.nextIcon || NEXT_ICON;

    this.init();
  }

  /** The P1-P3 urls of the plan a landing slug names. */
  private urlsFor(slug: string): string[] {
    const plan = this.catalog.homePlans.find((entry) => entry.slug === slug);

    if (!plan) return [];

    return IMAGE_FIELDS.map((field) => plan.fields[field])
      .filter(isAsset)
      .map((asset) => asset.url);
  }

  private init(): void {
    const cards = Array.from(document.querySelectorAll<HTMLElement>(this.cardSelector));

    cards.forEach((card) => {
      const link = card.querySelector<HTMLAnchorElement>(PLAN_LINK);
      const wrap = card.querySelector<HTMLElement>('.kl-card__image-wrap');
      const image = wrap?.querySelector<HTMLImageElement>('img');

      if (!link || !wrap || !image) return;

      const slug = link.pathname.split('/').filter(Boolean).pop() || '';
      const urls = this.urlsFor(slug);

      if (urls.length < 2) {
        this.log('CardCarousel: not enough images for', slug);
        return;
      }

      this.attach(wrap, image, urls);
    });

    this.log('CardCarousel initialized over', cards.length, 'card(s)');
  }

  private attach(wrap: HTMLElement, image: HTMLImageElement, urls: string[]): void {
    let at = 0;

    wrap.classList.add('cc-wrap');

    const show = (next: number): void => {
      at = (next + urls.length) % urls.length;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = urls[at];
    };
    const arrow = (direction: -1 | 1, label: string, className: string, icon: string): void => {
      const button = document.createElement('button');

      button.type = 'button';
      button.className = `cc-arrow ${className}`;
      button.setAttribute('aria-label', label);
      button.innerHTML = icon;

      // The card wraps its media in a link; the arrows must not follow it.
      const handler = (event: Event): void => {
        event.preventDefault();
        event.stopPropagation();
        show(at + direction);
      };

      button.addEventListener('click', handler);
      this.handlers.push({ element: button, handler });
      wrap.appendChild(button);
    };

    arrow(-1, 'Previous image', 'cc-arrow--prev', this.prevIcon);
    arrow(1, 'Next image', 'cc-arrow--next', this.nextIcon);
  }

  destroy(): void {
    this.handlers.forEach(({ element, handler }) => {
      element.removeEventListener('click', handler);
      element.remove();
    });
    this.handlers = [];
  }
}
